import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { Empty, Card } from "./UI";
import {
  ClipboardCheck, FileSignature, NotebookPen, CheckCircle2, Clock, AlertTriangle,
  ExternalLink, FileText, User,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  urgent: "bg-[#FFEBEE] text-[#C62828]",
  high: "bg-[#FFF3E0] text-[#8D5A00]",
  medium: "bg-[#F5F2EB] text-[#6D4C41]",
  low: "bg-[#EFEBE0] text-[#7B6157]",
};

function StatCard({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className={`rounded-2xl border p-4 ${tone}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1"><Icon size={15} /><span className="text-[11px] font-bold uppercase tracking-wide">{label}</span></div>
      <div className="font-heading text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, testId }) {
  return (
    <div className="mb-3 flex items-center gap-2" data-testid={testId}>
      <Icon size={15} className="text-[#7B6157]" />
      <h3 className="font-heading text-base font-bold text-[#3E2723]">{title}</h3>
      {subtitle && <span className="text-xs text-[#7B6157]">· {subtitle}</span>}
    </div>
  );
}

function TaskRow({ t }) {
  const done = t.status === "completed";
  return (
    <div data-testid={`onboarding-task-${t.id}`}
      className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5 ${
        done ? "border-[#C8E6C9] bg-[#F1F8E9]"
        : t.overdue ? "border-[#FFCDD2] bg-[#FFF5F5]"
        : "border-[#EFEBE0] bg-white"}`}>
      {done ? <CheckCircle2 size={16} className="text-[#2E7D32]" />
        : t.overdue ? <AlertTriangle size={16} className="text-[#C62828]" />
        : <Clock size={16} className="text-[#7B6157]" />}
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-semibold ${done ? "text-[#5D4037] line-through" : "text-[#3E2723]"}`}>{t.title}</div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#7B6157]">
          {t.due_date && <span>Due {dayjs(t.due_date).format("MMM D, YYYY")}</span>}
          {t.assignee_name && <span>· {t.assignee_name}</span>}
          {t.template_key && <span className="rounded-full bg-[#EFEBE0] px-2 py-px font-semibold text-[#6D4C41]">{t.template_key}</span>}
        </div>
      </div>
      {!done && (
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold capitalize ${PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium}`}>{t.priority}</span>
      )}
      {t.overdue && !done && <span className="rounded-full bg-[#C62828] px-2 py-0.5 text-[10px] font-bold text-white">Overdue</span>}
    </div>
  );
}

function DocRow({ d, kind, onOpen }) {
  const signed = d.signed;
  return (
    <div data-testid={`onboarding-${kind}-${d.id}`}
      className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5 ${
        signed ? "border-[#C8E6C9] bg-[#F1F8E9]" : "border-[#FFECB3] bg-[#FFF8E1]"}`}>
      {signed ? <CheckCircle2 size={16} className="text-[#2E7D32]" /> : <FileSignature size={16} className="text-[#8D5A00]" />}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#3E2723]">{d.template_name}</div>
        <div className="text-[11px] text-[#7B6157]">
          {signed ? (
            <>Signed {d.signed_at ? dayjs(d.signed_at).format("MMM D, YYYY") : dayjs(d.created_at).format("MMM D, YYYY")}
              {d.signed_via === "client_portal" && <span className="ml-1 rounded bg-[#E0F2F1] px-1.5 py-px font-semibold text-[#00695C]">via portal</span>}</>
          ) : (
            <>Created {dayjs(d.created_at).format("MMM D, YYYY")} · Awaiting signature</>
          )}
        </div>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${signed ? "bg-[#2E7D32] text-white" : "bg-[#B26A00] text-white"}`}>
        {signed ? "Completed" : "Pending"}
      </span>
      {onOpen && (
        <button data-testid={`onboarding-${kind}-open-${d.id}`} onClick={() => onOpen(d)}
          className="rounded-full border border-[#E5DFD3] px-3 py-1 text-[11px] font-bold text-[#6D4C41] hover:bg-[#F5F2EB]">
          <ExternalLink size={12} className="inline mr-1" /> Open
        </button>
      )}
    </div>
  );
}

export function ClientOnboarding({ clientId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = () => api.get(`/clients/${clientId}/onboarding`).then((r) => setData(r.data))
    .catch((e) => toast.error(apiError(e)));
  useEffect(() => { if (clientId) load(); }, [clientId]); // eslint-disable-line

  if (!data) return <div className="h-32 animate-pulse rounded-2xl bg-[#EFEBE9]" />;
  const c = data.counts;
  const totalPending = c.tasks_open + c.consents_pending + c.assessments_pending + c.forms_open;
  const totalDone = c.tasks_done + c.consents_signed + c.assessments_signed + c.forms_signed;
  const totalOverall = totalPending + totalDone;
  const pct = totalOverall ? Math.round((totalDone / totalOverall) * 100) : 0;

  const pendingTasks = data.tasks.filter((t) => t.status !== "completed");
  const doneTasks = data.tasks.filter((t) => t.status === "completed");
  const pendingConsents = data.consents.filter((x) => !x.signed);
  const signedConsents = data.consents.filter((x) => x.signed);
  const pendingAssessments = data.assessments.filter((x) => !x.signed);
  const signedAssessments = data.assessments.filter((x) => x.signed);

  return (
    <div className="space-y-6" data-testid="client-onboarding-panel">
      <div className="rounded-2xl bg-gradient-to-br from-[#4E342E] to-[#3E2723] p-5 text-white">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase text-white/60">Onboarding Progress</div>
            <div className="font-heading text-2xl font-bold">{totalDone} of {totalOverall} items complete</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl font-black tabular-nums">{pct}%</div>
            <div className="text-[10px] font-bold uppercase text-white/60">Overall</div>
          </div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full bg-[#FFB300] transition-all" style={{ width: `${pct}%` }} data-testid="onboarding-progress-bar" />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={ClipboardCheck} label="Intake Tasks" testId="onboarding-stat-tasks"
          value={`${c.tasks_done} / ${c.tasks_done + c.tasks_open}`}
          tone={c.tasks_overdue > 0 ? "border-[#FFCDD2] bg-[#FFEBEE] text-[#C62828]" : "border-[#EFEBE0] bg-white text-[#4E342E]"} />
        <StatCard icon={FileSignature} label="Consent Forms" testId="onboarding-stat-consents"
          value={`${c.consents_signed} / ${c.consents_signed + c.consents_pending}`}
          tone="border-[#EFEBE0] bg-white text-[#4E342E]" />
        <StatCard icon={NotebookPen} label="Assessments" testId="onboarding-stat-assessments"
          value={`${c.assessments_signed} / ${c.assessments_signed + c.assessments_pending}`}
          tone="border-[#EFEBE0] bg-white text-[#4E342E]" />
        <StatCard icon={FileText} label="Clinical Forms" testId="onboarding-stat-forms"
          value={`${c.forms_signed} / ${c.forms_signed + c.forms_open}`}
          tone="border-[#EFEBE0] bg-white text-[#4E342E]" />
      </div>

      <Card className="p-5">
        <SectionHeader icon={ClipboardCheck} title="Intake Tasks" testId="onboarding-tasks-header"
          subtitle={pendingTasks.length ? `${pendingTasks.length} pending${c.tasks_overdue ? `, ${c.tasks_overdue} overdue` : ""}` : "All caught up"} />
        {pendingTasks.length === 0 && doneTasks.length === 0 ? (
          <Empty text="No intake tasks assigned. Use the Compliance Templates panel to apply one." />
        ) : (
          <div className="space-y-4">
            {pendingTasks.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase text-[#8D5A00]">Pending ({pendingTasks.length})</div>
                <div className="space-y-2" data-testid="onboarding-tasks-pending">
                  {pendingTasks.map((t) => <TaskRow key={t.id} t={t} />)}
                </div>
              </div>
            )}
            {doneTasks.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase text-[#2E7D32]">Completed ({doneTasks.length})</div>
                <div className="space-y-2" data-testid="onboarding-tasks-done">
                  {doneTasks.slice(0, 8).map((t) => <TaskRow key={t.id} t={t} />)}
                  {doneTasks.length > 8 && <div className="pl-2 text-xs text-[#7B6157]">+{doneTasks.length - 8} more completed</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <SectionHeader icon={FileSignature} title="Consent Forms" testId="onboarding-consents-header"
          subtitle={pendingConsents.length ? `${pendingConsents.length} awaiting signature` : "All signed"} />
        {data.consents.length === 0 ? (
          <Empty text="No consent forms have been created for this client yet." />
        ) : (
          <div className="space-y-4">
            {pendingConsents.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase text-[#8D5A00]">Pending ({pendingConsents.length})</div>
                <div className="space-y-2" data-testid="onboarding-consents-pending">
                  {pendingConsents.map((d) => (
                    <DocRow key={d.id} d={d} kind="consent" onOpen={() => navigate(`/clinical/consents?client=${clientId}`)} />
                  ))}
                </div>
              </div>
            )}
            {signedConsents.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase text-[#2E7D32]">Signed ({signedConsents.length})</div>
                <div className="space-y-2" data-testid="onboarding-consents-done">
                  {signedConsents.slice(0, 8).map((d) => (
                    <DocRow key={d.id} d={d} kind="consent" onOpen={() => navigate(`/clinical/consents?client=${clientId}`)} />
                  ))}
                  {signedConsents.length > 8 && <div className="pl-2 text-xs text-[#7B6157]">+{signedConsents.length - 8} more signed</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {data.assessments.length > 0 && (
        <Card className="p-5">
          <SectionHeader icon={NotebookPen} title="Assessments" testId="onboarding-assessments-header"
            subtitle={pendingAssessments.length ? `${pendingAssessments.length} in draft` : "All signed"} />
          <div className="space-y-4">
            {pendingAssessments.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase text-[#8D5A00]">In Progress ({pendingAssessments.length})</div>
                <div className="space-y-2" data-testid="onboarding-assessments-pending">
                  {pendingAssessments.map((d) => (
                    <DocRow key={d.id} d={d} kind="assessment" onOpen={() => navigate(`/clinical/assessments/${d.id}`)} />
                  ))}
                </div>
              </div>
            )}
            {signedAssessments.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase text-[#2E7D32]">Signed ({signedAssessments.length})</div>
                <div className="space-y-2" data-testid="onboarding-assessments-done">
                  {signedAssessments.map((d) => (
                    <DocRow key={d.id} d={d} kind="assessment" onOpen={() => navigate(`/clinical/assessments/${d.id}`)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {data.forms.length > 0 && (
        <Card className="p-5">
          <SectionHeader icon={FileText} title="Clinical Forms" testId="onboarding-forms-header"
            subtitle={`${c.forms_signed} signed of ${c.forms_signed + c.forms_open}`} />
          <div className="space-y-2" data-testid="onboarding-forms-list">
            {data.forms.slice(0, 12).map((d) => (
              <DocRow key={d.id} d={d} kind="form" onOpen={() => navigate(`/clinical-forms?client=${clientId}&form=${d.id}`)} />
            ))}
            {data.forms.length > 12 && <div className="pl-2 text-xs text-[#7B6157]">+{data.forms.length - 12} more forms</div>}
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3 text-xs text-[#7B6157]" data-testid="onboarding-portal-hint">
        <User size={13} />
        The client sees the same view (read-only) on their portal Home tab, and can sign pending consents themselves.
      </div>
    </div>
  );
}
